import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { copy } from "../copy";
import { formatMoney } from "../format";
import { contestHref, type LandingLiveContest, type LandingWin } from "../feed";

const MINUTE = 60_000;
const HOUR = 3_600_000;
const DAY = 24 * HOUR;

// Compact time left. Under a day stays in hours or minutes. A day or more
// switches to days, and a week or more switches to weeks.
function closingLabel(endsAt: string): string {
  const diff = new Date(endsAt).getTime() - Date.now();
  const days = Math.floor(diff / DAY);
  const weeks = Math.floor(days / 7);
  if (weeks >= 1) {
    const remDays = days % 7;
    return remDays > 0 ? `${weeks}w ${remDays}d` : `${weeks}w`;
  }
  if (days >= 1) return `${days}d`;
  const hours = Math.floor(diff / HOUR);
  if (hours >= 1) return `${hours}h`;
  return `${Math.max(1, Math.floor(diff / MINUTE))}m`;
}

function ordinal(n: number): string {
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${n}th`;
  switch (n % 10) {
    case 1:
      return `${n}st`;
    case 2:
      return `${n}nd`;
    case 3:
      return `${n}rd`;
    default:
      return `${n}th`;
  }
}

function showWin(win: LandingWin): boolean {
  const place = win.placement ?? 0;
  if (place > 1) return true;
  return place === 1 || (win.payout_cents ?? 0) > 0;
}

function winNode(win: LandingWin): ReactNode {
  const place = win.placement ?? 0;
  if (place > 1) {
    return (
      <span className="text-chrome">
        <span className="text-chrome-dim">{copy.ticker.producer} </span>
        <span className="font-bold">{win.producer_username}</span>
        <span className="text-chrome-dim"> {copy.ticker.camePlace(ordinal(place))}</span>
      </span>
    );
  }
  const amount = (win.payout_cents ?? 0) > 0 ? formatMoney(win.payout_cents!) : "";
  return (
    <span>
      <span className="text-chrome-dim">{copy.ticker.producer} </span>
      <span className="font-bold text-money">{win.producer_username}</span>
      <span className="text-chrome-dim"> {copy.ticker.won}</span>
      {amount ? <span className="bf-tabular font-bold text-gold"> {amount}</span> : null}
    </span>
  );
}

// Full-bleed strip. Paid producer wins and open contests are interleaved, and
// the track is rendered twice so translateX(-50%) loops with no seam. With
// reduced motion the track stops and the duplicate drops out.
export default function WinTicker({ wins, contests }: { wins: LandingWin[]; contests: LandingLiveContest[] }) {
  const shown = wins.filter(showWin).sort((a, b) => {
    const byTime = new Date(b.at).getTime() - new Date(a.at).getTime();
    if (byTime !== 0) return byTime;
    return (a.placement ?? 1) - (b.placement ?? 1);
  });
  const now = Date.now();
  const open = contests.filter((c) => new Date(c.ends_at).getTime() > now);

  const items: { key: string; href?: string; node: ReactNode }[] = [];
  for (let i = 0; i < Math.max(shown.length, open.length); i += 1) {
    const win = shown[i];
    if (win) {
      items.push({
        key: `win:${win.producer_username}:${win.placement ?? 0}:${win.at}`,
        href: win.contest_slug ? contestHref(win.contest_slug) : undefined,
        node: winNode(win),
      });
    }
    const contest = open[i];
    if (contest) {
      items.push({
        key: `closing:${contest.slug}`,
        href: contestHref(contest.slug),
        node: (
          <>
            <span className="font-bold uppercase tracking-[0.1em] text-chrome">{contest.name}</span>
            <span className="text-chrome-dim">{copy.ticker.closesIn}</span>
            <span className="bf-tabular font-bold text-gold">{closingLabel(contest.ends_at)}</span>
          </>
        ),
      });
    }
  }

  // A section with no data does not render at all.
  if (items.length === 0) return null;

  const row = (dupe: boolean) =>
    items.map((item) => {
      const body = (
        <>
          {item.node}
          <span aria-hidden className="ml-3 text-gold/60">
            ◆
          </span>
        </>
      );
      return (
        <li
          key={`${dupe ? "dupe:" : ""}${item.key}`}
          aria-hidden={dupe || undefined}
          className={`flex shrink-0 items-center whitespace-nowrap border-r border-ground-edge text-[13px] ${
            dupe ? "motion-reduce:hidden" : ""
          }`}
        >
          {item.href ? (
            <Link
              to={item.href}
              tabIndex={dupe ? -1 : undefined}
              className="bf-focusable flex items-center gap-2 px-5 py-0.5 transition-colors hover:text-gold"
            >
              {body}
            </Link>
          ) : (
            <span className="flex items-center gap-2 px-5">{body}</span>
          )}
        </li>
      );
    });

  return (
    <aside
      data-testid="win-ticker"
      aria-label={copy.ticker.label}
      className="bf-marquee border-y border-ground-edge bg-ground-raised py-3"
    >
      <ul data-testid="ticker-track" className="bf-marquee-track">
        {row(false)}
        {row(true)}
      </ul>
    </aside>
  );
}
